import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

// TODO: Fetch mastery descriptions from the API instead of hardcoding them here.
/** @type {{[key: string]: string}} */
export const MASTERY_DESCRIPTIONS = {
  cleave:
    'On hit, make a melee attack against a second creature within 5 ft of the first, using the same modifier. Damage equals your ability modifier.',
  graze: 'On miss, deal damage equal to your ability modifier.',
  nick: 'Make an extra attack with a Light weapon as part of the Attack action (no ability modifier to damage).',
  push: 'On hit, push a Large or smaller creature 10 ft away from you.',
  sap: 'On hit, the creature has Disadvantage on its next attack roll before your next turn.',
  slow: "On hit, the creature's speed is reduced by 10 ft until your next turn.",
  topple:
    'On hit, the creature must succeed on a CON save (DC = 8 + your ability modifier + proficiency) or have the Prone condition.',
  vex: 'On hit, you have Advantage on your next attack roll against this creature before your next turn.',
};

const infoIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <circle cx="12" cy="12" r="10" />
  <path d="M12 16v-4" />
  <path d="M12 8h.01" />
</svg>`;

class WeaponOptionCard extends BaseComponent {
  static properties = {
    name: { type: String },
    slug: { type: String },
    masteryProperty: { type: String, attribute: 'mastery-property' },
    selected: { type: Boolean },
    disabled: { type: Boolean },
    recommended: { type: Boolean },
    inputType: { type: String, attribute: 'input-type' },
    radioGroup: { type: String, attribute: 'radio-group' },
  };

  static styles = css`
    :host {
      display: block;
    }
    .card {
      cursor: pointer;
      transition:
        border-color 0.15s,
        box-shadow 0.15s;
      height: 100%;
    }
    .card:hover:not(.disabled) {
      border-color: #0d6efd;
    }
    .card.selected {
      border-color: #0d6efd;
      box-shadow: 0 0 0 1px #0d6efd;
      background-color: rgba(13, 110, 253, 0.04);
    }
    .card.disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .info-icon {
      color: #6c757d;
      cursor: pointer;
    }
  `;

  constructor() {
    super();
    this.name = '';
    this.slug = '';
    this.masteryProperty = '';
    this.selected = false;
    this.disabled = false;
    this.recommended = false;
    this.inputType = 'radio';
    this.radioGroup = '';
  }

  _onClick() {
    if (this.disabled) return;
    this.dispatchEvent(
      new CustomEvent('card-selected', {
        detail: { slug: this.slug },
        bubbles: true,
        composed: true,
      }),
    );
  }

  updated() {
    this._initTooltips();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._disposeTooltips();
  }

  _initTooltips() {
    this._disposeTooltips();
    const Tooltip = /** @type {{bootstrap?: {Tooltip: {new(el: Element, opts?: object): {dispose(): void}}}}} */ (
      window
    ).bootstrap?.Tooltip;
    if (!Tooltip) return;
    /** @type {{dispose: Function}[]} */
    this._tooltipInstances = [];
    const icons = this.renderRoot?.querySelectorAll('[data-bs-toggle="tooltip"]') || [];
    icons.forEach((el) => {
      this._tooltipInstances?.push(new Tooltip(el, { container: 'body' }));
    });
  }

  _disposeTooltips() {
    if (this._tooltipInstances) {
      this._tooltipInstances.forEach((t) => {
        t.dispose();
      });
      this._tooltipInstances = undefined;
    }
  }

  render() {
    const masteryLabel = this.masteryProperty
      ? this.masteryProperty.charAt(0).toUpperCase() + this.masteryProperty.slice(1)
      : '';
    const masteryDesc = MASTERY_DESCRIPTIONS[this.masteryProperty];

    return html`
      <div class="card ${this.selected ? 'selected' : ''} ${this.disabled ? 'disabled' : ''}" @click="${this._onClick}">
        <div class="card-body py-2 px-3">
          <div class="d-flex align-items-center">
            <input
              type="${this.inputType}"
              class="form-check-input me-2"
              .checked="${this.selected}"
              ?disabled="${this.disabled}"
              name="${this.radioGroup}"
              @click="${(/** @type {Event} */ e) => {
                e.stopPropagation();
                this._onClick();
              }}"
            />
            <span class="fw-medium">${this.name}</span>${
              this.recommended
                ? html`<span class="badge bg-primary bg-opacity-25 text-primary ms-2">Recommended</span>`
                : ''
            }
            ${
              masteryLabel
                ? html` <span class="badge bg-secondary bg-opacity-25 text-dark ms-1">${masteryLabel}</span> `
                : ''
            }
            ${
              masteryDesc
                ? html`
                  <span class="ms-auto info-icon" data-bs-toggle="tooltip" data-bs-title="${masteryDesc}">
                    ${infoIcon}
                  </span>
                `
                : ''
            }
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('weapon-option-card', WeaponOptionCard);
